import { differenceInCalendarDays, format } from "date-fns";

import { getCollapsedBudgetTotal } from "../../../components/novos/Budget/RightSideBar/BudgetSummary/collapsed-totals";
import { getSummaryTotals } from "../../../components/Summary/utils";
import { TabActive } from "../../../types";
import {
  BudgetType,
  getBudgetCurrentStatus,
  type StatusBudget,
} from "../../../types/budget/budget";
import type { BudgetListItem } from "../../../utils/budget-adapters";
import { TIMELINE_TRACK_VALUES } from "../../../utils/projectStatusTimeline";

/**
 * Regras da aba Overview do projeto (MMV-2681). Tudo aqui é puro para ser
 * testado sem montar a página; os componentes só formatam.
 */

// ---------------------------------------------------------------------------
// Próximo passo
// ---------------------------------------------------------------------------

const BUDGET_STAGE_STATUSES = ["BUDGET", "ON_APROVAL"];

/** Status do pipeline a partir do Aprovado: o próximo passo é o Fechamento. */
const CLOSING_STAGE_STATUSES = TIMELINE_TRACK_VALUES.filter(
  (status) => !BUDGET_STAGE_STATUSES.includes(status)
) as string[];

/**
 * Aba que recebe o indicador de próximo passo. A spec fixa Orçamento para
 * Orçamento/Em Aprovação e Fechamento para Pré-fechamento, Faturado, Auditado
 * e Prestação de Contas ("a definir com o time" para o resto). Os demais status
 * do pipeline depois do Aprovado também apontam para Fechamento; status fora
 * do pipeline (Cancelado, Perdido, Declinado…) não têm próximo passo.
 */
export function getNextStepTab(
  projectStatus: string | undefined | null
): TabActive | null {
  if (!projectStatus) return null;
  if (BUDGET_STAGE_STATUSES.includes(projectStatus)) return TabActive.BUDGET;
  if (CLOSING_STAGE_STATUSES.includes(projectStatus)) return TabActive.APPROVED;
  return null;
}

// ---------------------------------------------------------------------------
// Resumo financeiro
// ---------------------------------------------------------------------------

type SummaryKey = "ON_APPROVAL" | "APPROVED" | "CLOSURE" | "AUDITED";

export interface FinancialStageDefinition {
  key: "BRIEFING" | SummaryKey;
  label: string;
  /** Mesma cor do cabeçalho do acordeão na aba Resumo Financeiro. */
  color: string;
  /** Primeiro status do projeto em que a etapa conta como alcançada. */
  reachedFrom: string;
}

export const FINANCIAL_STAGES: FinancialStageDefinition[] = [
  {
    key: "BRIEFING",
    label: "Briefing - Dados do Evento",
    color: "#3F485A",
    reachedFrom: "BUDGET",
  },
  {
    key: "ON_APPROVAL",
    label: "Em aprovação",
    color: "#D09C17",
    reachedFrom: "ON_APROVAL",
  },
  {
    key: "APPROVED",
    label: "Aprovado",
    color: "#0D9813",
    reachedFrom: "APPROVED",
  },
  {
    key: "CLOSURE",
    label: "Fechamento",
    color: "#1370A4",
    reachedFrom: "PRE_CLOSING",
  },
  {
    key: "AUDITED",
    label: "Auditado",
    color: "#738696",
    reachedFrom: "AUDITED",
  },
];

/**
 * Status fora do pipeline principal, traduzidos para o ponto do pipeline até
 * onde o projeto chegou. Os não listados (Cancelado, Perdido, Declinado,
 * Duplicidade, BID, Compartilhado Financeiro) ficam só no Briefing.
 */
const OFF_TRACK_EQUIVALENT: Record<string, string> = {
  APPROVED_AND_CANCELED: "APPROVED",
  CANCELED_AND_CHARGED: "APPROVED",
  CANCELED_AND_AUDITED: "AUDITED",
};

function trackIndex(status: string | undefined | null): number {
  if (!status) return 0;
  const equivalent = OFF_TRACK_EQUIVALENT[status] ?? status;
  const index = (TIMELINE_TRACK_VALUES as readonly string[]).indexOf(
    equivalent
  );
  return Math.max(index, 0);
}

export function isFinancialStageReached(
  stage: FinancialStageDefinition,
  projectStatus: string | undefined | null
): boolean {
  return trackIndex(projectStatus) >= trackIndex(stage.reachedFrom);
}

export interface FinancialStageRow extends FinancialStageDefinition {
  reached: boolean;
  isCurrent: boolean;
  /** `null` = etapa não alcançada (exibe "—"). */
  value: number | null;
  /**
   * Total de receitas da etapa, recalculado com o mesmo `getSummaryTotals` do
   * acordeão na aba Resumo Financeiro. `null` = etapa não alcançada ou sem
   * resumo financeiro (o Briefing só tem a verba do cliente).
   */
  revenue: number | null;
}

export function buildFinancialStageRows(params: {
  projectStatus: string | undefined | null;
  summary: Partial<Record<SummaryKey, any>> | undefined;
  customerBudget: number | undefined;
}): FinancialStageRow[] {
  const reachedFlags = FINANCIAL_STAGES.map((stage) =>
    isFinancialStageReached(stage, params.projectStatus)
  );
  const currentIndex = reachedFlags.lastIndexOf(true);

  return FINANCIAL_STAGES.map((stage, index) => {
    const reached = reachedFlags[index];
    const stageSummary =
      stage.key === "BRIEFING" ? undefined : params.summary?.[stage.key];
    const rawValue =
      stage.key === "BRIEFING"
        ? params.customerBudget
        : stageSummary?.totalValueOfEvents;
    const revenue = stageSummary
      ? getSummaryTotals(stageSummary).totalRevenue
      : NaN;

    return {
      ...stage,
      reached,
      isCurrent: index === currentIndex,
      value: reached ? Number(rawValue) || 0 : null,
      revenue:
        reached && stage.key !== "BRIEFING"
          ? Number.isFinite(revenue)
            ? revenue
            : 0
          : null,
    };
  });
}

export interface AuditResults {
  available: boolean;
  loss: number | null;
  profitability: number | null;
}

/**
 * Prejuízo e rentabilidade só valem depois da auditoria. A rentabilidade é
 * recalculada com o mesmo `getSummaryTotals` do acordeão Auditado, para bater
 * com o que a aba Resumo Financeiro mostra.
 */
export function getAuditResults(
  projectStatus: string | undefined | null,
  auditedSummary: Record<string, any> | undefined
): AuditResults {
  const auditedStage = FINANCIAL_STAGES[FINANCIAL_STAGES.length - 1];
  if (!isFinancialStageReached(auditedStage, projectStatus) || !auditedSummary) {
    return { available: false, loss: null, profitability: null };
  }

  const { profitability } = getSummaryTotals(auditedSummary);
  return {
    available: true,
    loss: Number(auditedSummary.loss) || 0,
    profitability: Number.isFinite(profitability) ? profitability : 0,
  };
}

// ---------------------------------------------------------------------------
// Orçamentos
// ---------------------------------------------------------------------------

export type StatusTone = "green" | "orange" | "blue" | "red" | "gray";

export interface BudgetStatusDisplay {
  label: string;
  tone: StatusTone;
}

/**
 * Status efetivo = última entrada do versionHistory. Não usar
 * `currentVersionStatus` do resumo v2: ele segue o `negotiationVersion`, que
 * fica parado na negociação depois que o orçamento é aprovado (versão seguinte).
 */
export function getBudgetEffectiveStatus(row: BudgetListItem): StatusBudget {
  return getBudgetCurrentStatus(row.versionHistory);
}

/**
 * O orçamento não tem um status "Em aprovação" próprio (o versionHistory só
 * guarda OPEN, NEGOTIATE, APPROVED, DENIED, DENIED_CLOSING). Um orçamento
 * aberto vira "Em aprovação" enquanto o projeto está Em Aprovação.
 */
export function getBudgetStatusDisplay(
  row: BudgetListItem,
  projectStatus: string | undefined | null
): BudgetStatusDisplay {
  const status = getBudgetEffectiveStatus(row);
  switch (status) {
    case "APPROVED":
      return { label: "Aprovado", tone: "green" };
    case "NEGOTIATE":
      return { label: "Em negociação", tone: "orange" };
    case "DENIED":
    case "DENIED_CLOSING":
      return { label: "Reprovado", tone: "red" };
    case "OPEN":
      return projectStatus === "ON_APROVAL"
        ? { label: "Em aprovação", tone: "blue" }
        : { label: "Em orçamento", tone: "gray" };
    default:
      return { label: "Em orçamento", tone: "gray" };
  }
}

/** Orçamento que ainda depende de aprovação do cliente. */
export function isBudgetAwaitingApproval(row: BudgetListItem): boolean {
  const status = getBudgetEffectiveStatus(row);
  return status === "OPEN" || status === "NEGOTIATE";
}

function latestVersion(row: BudgetListItem): number {
  return (row.versionHistory ?? []).reduce(
    (max, entry) => Math.max(max, entry.version ?? 0),
    0
  );
}

/** Valor da etapa atual do orçamento (Orçamento, Negociação ou Fechamento). */
export function getBudgetRowValue(row: BudgetListItem): number {
  return getCollapsedBudgetTotal(
    row as never,
    getBudgetEffectiveStatus(row),
    latestVersion(row)
  );
}

export const BUDGET_TYPE_LABEL: Record<string, string> = {
  [BudgetType.LOGISTICA]: "Logística",
  [BudgetType.PRODUCAO]: "Produção",
};

/**
 * Numeração pela posição na lista, a mesma da lista lateral da aba Orçamento
 * ("Orçamento N"). `budgetIndex` conta também orçamentos apagados.
 */
export function getBudgetNumber(position: number): string {
  return String(position + 1).padStart(2, "0");
}

export function getBudgetName(row: BudgetListItem, position: number): string {
  return row.name || `Orçamento ${getBudgetNumber(position)}`;
}

// ---------------------------------------------------------------------------
// Pendências
// ---------------------------------------------------------------------------

/**
 * Antecedência (dias até o início do evento) a partir da qual o orçamento não
 * aprovado vira pendência crítica. A spec deixa o valor "a definir com o time".
 */
export const BUDGET_APPROVAL_WARNING_DAYS = 30;

export type PendencyKey = "budget-approval" | "event-data";
export type PendencySeverity = "critical" | "attention";

export interface Pendency {
  key: PendencyKey;
  title: string;
  severity: PendencySeverity;
  description: string;
}

export function buildPendencies(params: {
  projectStatus: string | undefined | null;
  eventStartDate: Date | string | undefined | null;
  eventDataConfirmedAt: string | null | undefined;
  budgets: BudgetListItem[];
  now?: Date;
}): Pendency[] {
  const now = params.now ?? new Date();
  const pendencies: Pendency[] = [];

  const startDate = params.eventStartDate
    ? new Date(params.eventStartDate)
    : null;
  const hasValidStart = !!startDate && !Number.isNaN(startDate.getTime());
  const daysToEvent = hasValidStart
    ? differenceInCalendarDays(startDate!, now)
    : null;

  const awaiting = params.budgets
    .map((row, position) => ({ row, position }))
    .filter(({ row }) => isBudgetAwaitingApproval(row));

  if (
    BUDGET_STAGE_STATUSES.includes(params.projectStatus ?? "") &&
    awaiting.length > 0 &&
    daysToEvent !== null &&
    daysToEvent <= BUDGET_APPROVAL_WARNING_DAYS
  ) {
    const names = awaiting.map(({ row, position }) =>
      getBudgetName(row, position)
    );
    const subject =
      names.length === 1
        ? `o ${names[0]} ainda está ${getBudgetStatusDisplay(
            awaiting[0].row,
            params.projectStatus
          ).label}`
        : `${names.length} orçamentos ainda não foram aprovados (${names.join(
            ", "
          )})`;
    const when =
      daysToEvent < 0
        ? `O evento começou em ${format(startDate!, "dd/MM")}`
        : `O evento começa em ${format(startDate!, "dd/MM")}`;

    pendencies.push({
      key: "budget-approval",
      title: "Orçamento pendente de aprovação",
      severity: "critical",
      description: `${when} e ${subject}.`,
    });
  }

  if (!params.eventDataConfirmedAt) {
    pendencies.push({
      key: "event-data",
      title: "Dados do evento precisam ser revisados",
      severity: "attention",
      description: "Confirme o tipo do evento e a quantidade de pessoas.",
    });
  }

  return pendencies;
}

export function countPendencies(pendencies: Pendency[]): {
  critical: number;
  attention: number;
} {
  return {
    critical: pendencies.filter((p) => p.severity === "critical").length,
    attention: pendencies.filter((p) => p.severity === "attention").length,
  };
}

// ---------------------------------------------------------------------------
// Formatação
// ---------------------------------------------------------------------------

function toDate(value: Date | string | undefined | null): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** "15/10 a 18/10/2026", com "(4 dias)" quando `withDuration`. */
export function formatEventPeriod(
  start: Date | string | undefined | null,
  end: Date | string | undefined | null,
  withDuration = false
): string {
  const startDate = toDate(start);
  const endDate = toDate(end);
  if (!startDate) return "";
  if (!endDate) return format(startDate, "dd/MM/yyyy");

  const sameDay = differenceInCalendarDays(endDate, startDate) === 0;
  const period = sameDay
    ? format(startDate, "dd/MM/yyyy")
    : `${format(
        startDate,
        startDate.getFullYear() === endDate.getFullYear()
          ? "dd/MM"
          : "dd/MM/yyyy"
      )} a ${format(endDate, "dd/MM/yyyy")}`;

  if (!withDuration) return period;
  const days = differenceInCalendarDays(endDate, startDate) + 1;
  return `${period} (${days} ${days === 1 ? "dia" : "dias"})`;
}

export function formatEventLocation(event: {
  city?: string;
  state?: string;
  local?: string;
} | undefined): string {
  if (!event) return "";
  const cityState = [event.city, event.state].filter(Boolean).join("/");
  return cityState || event.local || "";
}
